import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import clsx from 'clsx';
import { Trans, useTranslation } from 'react-i18next';
import { useMediaQuery, useTheme, Typography, Grid } from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';

// CONSTANTS
import { ROUTE_PROFILE_MY_APPS } from '@/constants/routes';

// UTILITY
import { updateCarouselOptions } from '@/utility/content';

// STORE
import { AppDetailsActions, UserActions } from '@/store/actions';

// COMPONENTS
import SectionTitleLink from '@/components/SectionTitleLink';
import StageCarousel from '@/components/Carousels/StageSwiperCarousel';
import { SwiperSlide } from '@/components/Carousels/SwiperCarousel';
import AppCardSecondary from '@/components/Cards/AppCardSecondary';
import ChangePriceDialog from '@/components/Dialogs/DialogChangePrice';
import MyAppCardSkeleton from '@/components/Skeletons/MyAppCardSkeleton';

import { Container, CarouselWrapper } from './styles';
import useActions from '@/hooks/useActions';

const carouselOptions = {
  spaceBetween: 30,
  breakpoints: {
    0: {
      slidesPerView: 'auto',
      slidesPerGroup: 1,
      spaceBetween: 16,
    },
    600: {
      slidesPerView: 2,
      slidesPerGroup: 2,
    },
    750: {
      slidesPerView: 3,
      slidesPerGroup: 3,
    },
    960: {
      slidesPerView: 4,
      slidesPerGroup: 4,
      allowTouchMove: false,
    },
  },
};

const MyAppsCarousel = props => {
  const {
    data,
    title,
    link,
    stylesType,
    loading,
    hideMobileNav,
    analyticsLocation,
    onRemoveStatus,
  } = props;
  const [t] = useTranslation();
  const { unique_id, anonymousId, partner, statusesLoadings, updatePriceLoading } = useSelector(
    ({ auth, partner: partnerStore, user, appDetails }) => ({
      unique_id: auth.unique_id,
      anonymousId: auth.anonymousId,
      partner: partnerStore.data,
      statusesLoadings: appDetails.loadings.statusesLoadings,
      updatePriceLoading: user.loadings.updatePrice,
    }),
  );

  const [appListRemoveStatus, userChangeAppPrice] = useActions([
    (app_id, callback) => AppDetailsActions.appListRemoveStatus.action({ app_id, callback }),
    submitData => UserActions.userChangeAppPrice.action({ data: submitData }),
  ]);

  const [state, setState] = useState({
    showChangePrice: false,
    changePriceData: {},
    carouselOptions: hideMobileNav ? updateCarouselOptions(carouselOptions) : carouselOptions,
  });
  const theme = useTheme();
  const downMd = useMediaQuery(theme.breakpoints.down('md'));
  const downXs = useMediaQuery(theme.breakpoints.down('xs'));
  const hasApps = !!data.length;

  React.useEffect(() => {
    if (hideMobileNav) {
      setState({ ...state, carouselOptions: updateCarouselOptions(carouselOptions) });
    }
  }, [hideMobileNav]);

  let updatedStylesType = stylesType;

  function handleRemoveAppListStatus(appData) {
    appListRemoveStatus(appData.id, () => {
      onRemoveStatus(appData, null);
    });
  }

  function manageChangePriceState(dialogState, dialogData = {}) {
    setState({ ...state, showChangePrice: dialogState, changePriceData: dialogData });
  }

  function handleChangePriceSubmit(appData, price) {
    const apps = [
      {
        id: appData.id,
        price,
      },
    ];

    userChangeAppPrice({ apps });
  }

  function getTotalPrice() {
    const totalCost = data.reduce((acc, curValue) => {
      const mainPrice = curValue.user_price !== null ? curValue.user_price : curValue.price;

      return acc + mainPrice;
    }, 0);

    return +totalCost.toFixed(2);
  }

  if (downMd && updatedStylesType === 'collections' && !hideMobileNav) {
    updatedStylesType = 'want-to-watch';
  }

  const skeletons = new Array(downXs ? 2 : 4).fill('');
  const skeletonsSection = (
    <>
      <Grid item xs={12}>
        <Skeleton width="40%" height={50} />
        <Skeleton width="40%" height={30} />
      </Grid>
      {skeletons.map((item, index) => {
        return (
          <Grid item xs={6} sm={3} key={index}>
            <MyAppCardSkeleton />
          </Grid>
        );
      })}
    </>
  );

  if (!hasApps && !loading) {
    return null;
  }

  return (
    <Container className={clsx('carousel-my-apps', `carousel-my-apps--${updatedStylesType}`)}>
      {loading ? (
        <Grid container spacing={2}>
          {skeletonsSection}
        </Grid>
      ) : (
        hasApps && (
          <>
            <div className="carousel-my-apps__title-wrap">
              {!!title && (
                <SectionTitleLink
                  className="carousel-my-apps__title"
                  title={title || t('profileMyApps.title.have')}
                  link={link || ROUTE_PROFILE_MY_APPS}
                  analyticsLocation={analyticsLocation}
                />
              )}
              <div className="carousel-my-apps__total-cost">
                <Typography variant="subtitle1" component="h2" color="textSecondary">
                  {t('profileMyApps.totalCost')}:{' '}
                  <Typography className="p4" component="span" color="textSecondary">
                    <Trans
                      i18nKey="common.costMonthWithTag"
                      values={{ cost: getTotalPrice() }}
                      components={{
                        strong: <Typography variant="h5" component="span" color="textPrimary" />,
                      }}
                    />
                  </Typography>
                </Typography>
              </div>
            </div>
            <CarouselWrapper>
              <StageCarousel carouselOptions={state.carouselOptions} stylesType={updatedStylesType}>
                {data.map(appData => (
                  <SwiperSlide key={appData.id}>
                    <AppCardSecondary
                      fullHeight
                      data={appData}
                      removeAppListStatus={handleRemoveAppListStatus}
                      statusesLoading={
                        statusesLoadings[appData.id] || updatePriceLoading[appData.id]
                      }
                      changePriceClick={manageChangePriceState}
                      listType="have"
                      unique_id={unique_id || anonymousId}
                      partner={partner}
                      location={analyticsLocation}
                      withShadow={false}
                    />
                  </SwiperSlide>
                ))}
              </StageCarousel>
            </CarouselWrapper>
            <ChangePriceDialog
              open={state.showChangePrice}
              data={state.changePriceData}
              onClose={() => manageChangePriceState(false)}
              onSaveClick={handleChangePriceSubmit}
            />
          </>
        )
      )}
    </Container>
  );
};

MyAppsCarousel.defaultProps = {
  data: [],
  title: '',
  link: '',
  stylesType: 'collections',
  onRemoveStatus: () => {},
  loading: false,
  hideMobileNav: false,
};

MyAppsCarousel.propTypes = {
  data: PropTypes.array,
  title: PropTypes.string,
  link: PropTypes.string,
  stylesType: PropTypes.string,
  onRemoveStatus: PropTypes.func,
  loading: PropTypes.bool,
  analyticsLocation: PropTypes.string,
  hideMobileNav: PropTypes.bool,
};

export default React.memo(MyAppsCarousel);
